import { getUrlWithVariant } from "@shopify/theme-product-form";
import isMobile from "is-mobile";
import { getVariantFromId } from "@shopify/theme-product";
import scrollContainer from "@/lib/scroll-container";
import getMediaQuery from "@/lib/media-queries";
import debounce from "@/utils/debounce";

import { add, listen, qs, qsa, remove, toggle } from "@fluorescent/dom";
import { cart, getProduct } from "@/glow";
import { emit } from "@/glow/events";

import ProductForm from "@/lib/product-form";
import productBlocks from "@/lib/product/product-blocks";
import productLightbox from "@/lib/product-lightbox";
import { switchImage } from "@/utils";
import {
  sidebarRecommendations,
  media,
  giftCardRecipient,
  stickyAtcBar,
} from "@/lib/product";
import StickyScroll from "@/lib/sticky-scroll";
import storeAvailability from "@/lib/store-availability";
import dispatchCustomEvent from "@/lib/dispatch-custom-event";
import srraf from "srraf";

const classes = {
  hidden: "hidden",
};

const selectors = {
  form: "[data-product-form]",
  addToCart: "[data-add-to-cart]",
  thumbs: "[data-product-thumbnails]",
  thumb: "[data-product-thumbnail]",
  thumbMobile: "[data-thumbnail-size='mobile'] [data-product-thumbnail]",
  mobileThumbById: id =>
    `[data-thumbnail-size='mobile'] [data-thumbnail-id='${id}']`,
  storeAvailability: "[data-store-availability-container]",
  quantityError: "[data-quantity-error]",
  photosMobile: ".product__media-container.below-mobile",
  photosDesktop: ".product__media-container.above-mobile",
  photosDesktopLargeImageContainer:
    ".product__media-container.above-mobile .product__media",
  productMedia: ".product__media",
  photosQuickDesktop: ".quick-product .quick-product__left",
  photosQuickMobile:
    ".quick-product .product__block--product-header-image-wrap",
  quickCart: ".quick-cart",
  purchaseConfirmation: ".purchase-confirmation-popup",
  giftCardRecipientContainer: ".product-form__gift-card-recipient",
  externalVideoOverlay: ".external-video-overlay",
};

class Product {
  constructor(node) {
    this.container = node;

    const {
      isFullProduct,
      isFeaturedProduct,
      enableStickyContainer,
      scrollerId,
      sectionId,
      enableMultipleVariantMedia,
      initialMediaId,
      productHandle,
    } = this.container.dataset;

    this.isQuickProduct = false;
    this.isFullProduct = isFullProduct;
    this.isFeaturedProduct = isFeaturedProduct;
    this.scrollerId = scrollerId;
    this.sectionId = sectionId;
    this.enableMultipleVariantMedia = enableMultipleVariantMedia == "true";
    this.previousMediaId = initialMediaId;
    this.productHandle = productHandle;

    if (this.container.hasAttribute("data-quick-product")) {
      this.isQuickProduct = true;
      this.quickProductType = this.container.dataset.quickProductType;
    }

    this.formElement = qs(selectors.form, this.container);
    this.quantityError = qs(selectors.quantityError, this.container);

    this.viewInYourSpace = qs("[data-in-your-space]", this.container);
    this.viewInYourSpaceWrap = qs(
      ".product__view-in-space-wrap",
      this.container
    );

    this.viewInYourSpace &&
      toggle(this.viewInYourSpaceWrap, "visible", isMobile());

    // Handle Surface pickup
    this.availability = null;
    this.storeAvailabilityContainer = qs(
      selectors.storeAvailability,
      this.container
    );

    if (this.formElement) {
      const { productHandle, currentProductId } = this.formElement.dataset;
      const product = getProduct(productHandle);

      product(data => {
        const variant = getVariantFromId(data, parseInt(currentProductId));

        this.productBlocks = productBlocks(this.container, variant);

        if (this.storeAvailabilityContainer && variant) {
          this.availability = storeAvailability(
            this.storeAvailabilityContainer,
            data,
            variant
          );
        }

        this.productForm = ProductForm(this.container, this.formElement, data, {
          onOptionChange: e => this.onOptionChange(e),
          onFormSubmit: e => this.onFormSubmit(e),
          onQuantityChange: e => this.onQuantityChange(e),
        });

        this.scrollThumbnails(variant);
      });
    } else {
      this.productBlocks = productBlocks(this.container, false);
    }

    this.sidebarRecommendations = sidebarRecommendations(this.container);

    if (enableStickyContainer === "true" && !isMobile()) {
      this.stickyScroll = StickyScroll(this.container);
    }

    this._loadMedia();

    // Gift card recipient
    this.giftCardRecipient = giftCardRecipient(this.container);

    // Sticky ATC Bar
    this.stickyAtcBar = stickyAtcBar(this.container);
  }

  // initialize media
  _loadMedia() {
    this.photosDesktop = qs(selectors.photosDesktop, this.container);
    this.photosDesktopLargeImageContainer = qs(
      selectors.photosDesktopLargeImageContainer,
      this.container
    );
    this.photosQuickDesktop = qs(selectors.photosQuickDesktop, this.container);
    this.photosQuickMobile = qs(selectors.photosQuickMobile, this.container);

    this.productMedia = media(this.container);
    this.externalVideoOverlayEls = qsa(
      selectors.externalVideoOverlay,
      this.container
    );

    productLightbox();
    this._initThumbnails();
    this._initEvents();
    this.setupMobileSlider();
  }

  // initialize thumbnails
  _initThumbnails() {
    this.productThumbnails = qsa(selectors.thumbs, this.container);
    this.productThumbnailItems = qsa(selectors.thumb, this.container);
    this.productThumbnailItemsMobile = qsa(
      selectors.thumbMobile,
      this.container
    );

    this.productThumbnailsScrollers = this.productThumbnails.map(set => {
      return scrollContainer.initScroller(set, this.isQuickProduct);
    });

    if (
      this.photosDesktop?.getAttribute("data-thumbnails-position") == "left"
    ) {
      if (!qs(".placeholder-image", this.photosDesktop)) {
        // We want the thumbnails container height to match the first image's
        // height. This seems overly complex in order to achieve this when
        // image shape is set to "natural"
        const firstVisibleImage =
          this.photosDesktopLargeImageContainer.querySelector(
            ".product__media-item:not(.hidden) img"
          );

        this.firstVisibleImageAspectRatio =
          firstVisibleImage &&
          firstVisibleImage.offsetWidth / firstVisibleImage.offsetHeight;

        this.setThumbnailsHeight = () => {
          if (this.firstVisibleImageAspectRatio) {
            this.photosDesktop.style.setProperty(
              "--first-large-image-height",
              `${
                this.photosDesktopLargeImageContainer.offsetWidth /
                this.firstVisibleImageAspectRatio
              }px`
            );
          }
        };

        this.widthWatcher = srraf(({ vw }) => {
          this.setThumbnailsHeight();
        });

        this.setThumbnailsHeight();
      }
    }
  }

  _initEvents() {
    this.events = [
      listen(this.productThumbnailItems, "click", e =>
        this._handleThumbnailClick(e)
      ),
      listen(this.productThumbnailItemsMobile, "click", e =>
        this._handleThumbnailClick(e)
      ),
      listen(this.externalVideoOverlayEls, "click", e =>
        this._handleVideoOverlayClick(e)
      ),
    ];
  }

  _handleThumbnailClick(e) {
    e.preventDefault();
    this.productMedia.pauseAllMedia();

    const {
      currentTarget: { dataset },
    } = e;

    // Immediately update the active state
    this.productThumbnailItems.forEach(thumb => remove(thumb, "active"));
    add(e.currentTarget, "active");

    const isMobileView = window.matchMedia(getMediaQuery("below-960")).matches;

    if (this.isQuickProduct) {
      if (this.quickProductType == "view") {
        const target = isMobileView
          ? this.photosQuickMobile
          : this.photosQuickDesktop;

        switchImage(target, dataset.thumbnailId, this.viewInYourSpace);
      }
    } else {
      const target = isMobileView ? this.photosMobile : this.photosDesktop;

      switchImage(target, dataset.thumbnailId, this.viewInYourSpace);

      dispatchCustomEvent(`scroll-slider-${this.scrollerId}:go-to-slide`, {
        slideIndex: dataset.thumbnailIndex,
      });
    }
  }

  _handleVideoOverlayClick(e) {
    e.preventDefault();
    e.srcElement.remove();
  }

  setupMobileSlider() {
    if (this.isQuickProduct) return;

    this.events.push(
      listen(
        document,
        `scroll-slider-${this.scrollerId}:slide-changed`,
        evt => {
          this.productMedia.pauseAllMedia();
          const activeSlide = evt.detail.currentElement;
          const activeSlideImage = qs(
            ".image, [data-media-type='video'], .shopify-model-viewer-ui, .js-youtube, .js-vimeo",
            activeSlide
          );
          const slideContainerMobile = qs(
            `${selectors.photosMobile} ${selectors.productMedia}`,
            this.container
          );

          if (this.viewInYourSpace) {
            if (activeSlide.dataset.mediaType === "model") {
              this.viewInYourSpace.setAttribute(
                "data-shopify-model3d-id",
                activeSlide.dataset.mediaItemId
              );
            }
          }

          // When we're in the editor and switch from desktop to mobile, the height gets calculated before the
          // content is properly in place and leaves a big gap below the image. This short timeout prevents that.
          if (Shopify.designMode) {
            setTimeout(() => {
              slideContainerMobile.style.height = `${activeSlideImage.clientHeight}px`;
            }, 60);
          } else {
            slideContainerMobile.style.height = `${activeSlideImage.clientHeight}px`;
          }

          if (this.productThumbnailItemsMobile.length) {
            debounce(() => {
              this.productThumbnailItemsMobile.forEach(thumb =>
                remove(thumb, "active")
              );

              const targetThumb = qs(
                selectors.mobileThumbById(activeSlide.dataset.mediaItemId),
                this.container
              );

              add(targetThumb, "active");
            }, 200)();
          }
        }
      )
    );
  }

  //  Refresh the product media and UI for a selected variant when Media Grouping is enabled
  //  Works for both Quick View and Full Product templates.
  _refreshOverviewWithVariant(variant_id) {
    let requestURL;

    if (this.isQuickProduct) {
      requestURL = `${window.theme.routes.products}/${this.productHandle}?view=quick-view&section_id=${this.sectionId}&variant=${variant_id}`;
    } else if (this.isFeaturedProduct) {
      requestURL = `${window.theme.routes.products}/${this.productHandle}/?section_id=${this.sectionId}&variant=${variant_id}`;
    } else {
      requestURL = `${window.location.pathname}?section_id=${this.sectionId}&variant=${variant_id}`;
    }

    const mediaContainers = [
      this.photosDesktop,
      this.photosMobile,
      this.photosQuickDesktop,
      this.photosQuickMobile,
    ].filter(Boolean); // remove undefined/null

    mediaContainers.forEach(container => {
      container.classList.add(classes.loading);
    });

    fetch(requestURL)
      .then(response => response.text())
      .then(text => {
        const source = new DOMParser().parseFromString(text, "text/html");

        // In Quick View, desktop and mobile use separate containers
        if (this.isQuickProduct) {
          const updatedDesktop = qs(".quick-product__left", source);
          const updatedMobile = qs(
            ".product__block--product-header-image-wrap",
            source
          );

          const quickDesktop = qs(".quick-product__left", this.container);
          const quickMobile = qs(
            ".product__block--product-header-image-wrap",
            this.container
          );

          // replace inner content for both mobile and desktop, if present.
          // this helps preserve state when switching between views (ie resizing window).
          if (updatedMobile && quickMobile) {
            quickMobile.innerHTML = updatedMobile.innerHTML;
          }
          if (updatedDesktop && quickDesktop) {
            quickDesktop.innerHTML = updatedDesktop.innerHTML;
          }
        } else {
          // On full product page and featured product section, update main product media container
          const updated = qs(".product__primary-left", source);
          const target = qs(".product__primary-left", this.container);
          if (target) {
            target.innerHTML = updated.innerHTML;
          }
        }

        // clean up
        // unload left column components
        this._unloadMedia();
        this.events.forEach(unsubscribe => unsubscribe());

        // re-initialize left column components
        this._loadMedia();
      })
      .catch(error => {
        throw error;
      });
  }

  // When the user changes a product option
  onOptionChange({ dataset: { variant }, srcElement }) {
    this.productBlocks.update({ variant, srcElement });

    dispatchCustomEvent("product:variant-change", { variant: variant });

    // Update product availability content
    this.availability && this.availability.update(variant);

    if (!variant) {
      this.availability && this.availability.unload();
      return;
    }

    // Update URL with selected variant
    if (!this.isQuickProduct && !this.isFeaturedProduct) {
      this.updateUrlWithVariant(variant.id);
    }

    // We need to dispatch an event so Shopify pay knows the form has changed
    this.formElement.dispatchEvent(new Event("change"));

    // Update selected variant image and thumb
    if (variant.featured_media) {
      this.productMedia.pauseAllMedia();
      if (
        this.enableMultipleVariantMedia &&
        variant.featured_media.id != this.previousMediaId
      ) {
        this._refreshOverviewWithVariant(variant.id);
        this.previousMediaId = variant.featured_media.id;
      } else if (this.isFullProduct) {
        const mobileSlidesWrap = qs(selectors.photosMobile, this.container);
        const targetSlide = qs(
          `[data-media-item-id="${variant.featured_media.id}"]`,
          mobileSlidesWrap
        );

        if (targetSlide) {
          const targetSlideIndex = [
            ...targetSlide.parentElement.children,
          ].indexOf(targetSlide);

          dispatchCustomEvent(`scroll-slider-${this.scrollerId}:go-to-slide`, {
            slideIndex: targetSlideIndex,
          });
        }

        const imagesWrap = qs(".product__media-container.above-mobile");

        if (imagesWrap.dataset.galleryStyle === "thumbnails") {
          switchImage(
            this.photosDesktop,
            variant.featured_media.id,
            this.viewInYourSpace
          );

          this.highlightActiveThumbnail(this.photosDesktop, variant);
          this.scrollThumbnails(variant);
        } else {
          const targetImage = qs(
            `.product__media-container.above-mobile [data-media-id="${variant.featured_media.id}"]`
          );

          if (
            this.isFeaturedProduct &&
            !window.matchMedia(getMediaQuery("below-960")).matches
          ) {
            this.switchCurrentImage(variant.featured_media.id);
          } else {
            targetImage.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "nearest",
            });
          }
        }
      } else {
        this.switchCurrentImage(variant.featured_media.id);

        if (window.matchMedia(getMediaQuery("below-960")).matches) {
          this.photosQuickMobile &&
            this.highlightActiveThumbnail(this.photosQuickMobile, variant);
        } else {
          this.photosQuickDesktop &&
            this.highlightActiveThumbnail(this.photosQuickDesktop, variant);
        }

        this.scrollThumbnails(variant);
      }
    }

    // Update sticky add-to-cart variant image and option values
    if (this.stickyAtcBar && !this.isQuickProduct) {
      if (variant.featured_media) {
        this.stickyAtcBar.switchCurrentImage(variant.featured_media.id);
      }

      this.stickyAtcBar.updateOptionValues(variant);
      this.stickyAtcBar.updatePrice(variant);
    }
  }

  updateUrlWithVariant(variantId) {
    const url = getUrlWithVariant(window.location.href, variantId);
    window.history.replaceState({ path: url }, "", url);
  }

  switchCurrentImage(id) {
    const imagesWraps = qsa(".product__media", this.container);
    imagesWraps.forEach(imagesWrap => switchImage(imagesWrap, id));
  }

  highlightActiveThumbnail(photos, variant) {
    const thumb = qs(
      `[data-thumbnail-id="${variant.featured_media.id}"]`,
      photos
    );

    this.productThumbnailItems.forEach(thumb => remove(thumb, "active"));
    add(thumb, "active");
  }

  scrollThumbnails(variant) {
    if (!variant.featured_media) return;

    this.productThumbnails.forEach((group, index) => {
      const groupThumb = qs(
        `[data-thumbnail-id="${variant.featured_media.id}"]`,
        group
      ).closest("li");

      this.productThumbnailsScrollers[index].scrollTo(groupThumb);
    });
  }

  // When user updates quantity
  onQuantityChange({ dataset: { variant, quantity } }) {
    // Adjust the hidden quantity input within the form
    const quantityInputs = [...qsa('[name="quantity"]', this.formElement)];
    quantityInputs.forEach(quantityInput => {
      quantityInput.value = quantity;
    });

    dispatchCustomEvent("product:quantity-update", {
      quantity: quantity,
      variant: variant,
    });
  }

  // When user submits the product form
  onFormSubmit(e) {
    const purchaseConfirmation = qs(selectors.purchaseConfirmation, document);
    const quickCart = qs(selectors.quickCart, document);
    const quickProductForm = e.target.closest(".quick-product");

    // If the quick cart or purchase confirmation aren't enabled, and we're not in a
    // quick view modal, just submit the form.
    //
    // If the quick cart or purchase confirmation aren't enabled, and we're in a
    // quick view modal without quick add enabled, just submit the form.
    //
    // Otherwise, add to cart with ajax and show the purchase confirmation or quick cart
    if (
      (!purchaseConfirmation && !quickCart && !quickProductForm) ||
      (!purchaseConfirmation &&
        !quickCart &&
        quickProductForm &&
        quickProductForm.dataset.quickAddEnabled === "false")
    )
      return;

    e.preventDefault();

    const buttonEls = qsa(selectors.addToCart, this.container);

    add(this.quantityError, "hidden");

    buttonEls.forEach(button => {
      add(button, "loading");
    });

    cart
      .addItem(this.formElement)
      .then(({ item }) => {
        buttonEls.forEach(button => {
          remove(button, "loading");
        });

        if (purchaseConfirmation) {
          emit("confirmation-popup:open", null, { product: item });
        } else {
          emit("quick-cart:open");
        }

        dispatchCustomEvent("cart:item-added", { product: item });
      })
      .catch(error => {
        cart.get(); // update local cart data

        if (error && error.message) {
          if (typeof error.message === "object") {
            const sectionID = qs(
              selectors.giftCardRecipientContainer,
              this.container
            ).dataset.sectionId;

            Object.entries(error.message).forEach(([key, value]) => {
              const errorMessageID = `display-gift-card-recipient-${key}-error--${sectionID}`;
              const errorMessage = qs(`#${errorMessageID}`, this.container);
              const errorInput = qs(
                `#display-gift-card-recipient-${key}--${sectionID}`,
                this.container
              );

              errorMessage.innerText = value;
              remove(errorMessage, "hidden");
              errorInput.setAttribute("aria-invalid", true);
              errorInput.setAttribute("aria-describedby", errorMessageID);
            });
          } else {
            this.quantityError.innerText = error.message;
            remove(this.quantityError, "hidden");
          }
        } else {
          this.quantityError.innerText = this.quantityError.getAttribute(
            "data-fallback-error-message"
          );
          remove(this.quantityError, "hidden");
        }

        const buttonEls = qsa(selectors.addToCart, this.container);

        remove(this.quantityError, "hidden");

        buttonEls.forEach(button => {
          remove(button, "loading");
        });
      });
  }

  _unloadMedia() {
    this.productMedia.pauseAllMedia();
    this.mobileSwiper?.destroy();
    this.productThumbnailsScrollers.forEach(scroller => scroller.unload());
  }

  unload() {
    this.productForm.destroy();
    this.productBlocks.unload();
    this.events.forEach(unsubscribe => unsubscribe());
    this.stickyScroll?.destroy();
    this.sidebarRecommendations?.unload();
    this.productThumbnailsScrollers.forEach(scroller => scroller.unload());
    this.giftCardRecipient?.unload();
    this.stickyAtcBar?.unload();
  }
}

export default Product;
